import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  HIGHLIGHT_CASE_QUERY_KEY,
  parseHighlightCaseIdFromSearch,
  searchWithoutKey,
} from "../utils/caseListHighlight";

interface CaseRow {
  id?: number;
}

/**
 * Reads `?highlightCaseId=` from the URL, applies a row class, scrolls into view,
 * strips the param from the URL, then clears the highlight after a short delay.
 */
export function useHighlightCaseRowFromQuery(
  caseDetails: CaseRow[],
  loading: boolean
) {
  const location = useLocation();
  const history = useHistory();
  const [highlightRowId, setHighlightRowId] = useState<number | null>(null);
  /** Avoid duplicate scroll when `caseDetails` refetches with the same highlight. */
  const lastScrolledHighlightIdRef = useRef<number | null>(null);

  useEffect(() => {
    const id = parseHighlightCaseIdFromSearch(location.search);
    if (id != null) {
      setHighlightRowId(id);
      lastScrolledHighlightIdRef.current = null;
    }
  }, [location.search]);

  useEffect(() => {
    if (highlightRowId == null || loading) return;
    if (lastScrolledHighlightIdRef.current === highlightRowId) return;

    const found = caseDetails.some((c) => c.id === highlightRowId);
    if (!found) {
      const t = window.setTimeout(() => {
        const clean = searchWithoutKey(location.search, HIGHLIGHT_CASE_QUERY_KEY);
        if (clean !== location.search) {
          history.replace({ pathname: location.pathname, search: clean });
        }
        setHighlightRowId(null);
      }, 0);
      return () => clearTimeout(t);
    }

    const scrollT = window.setTimeout(() => {
      const row = document.querySelector(
        `tr[data-row-key="${highlightRowId}"]`
      );
      row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      const clean = searchWithoutKey(location.search, HIGHLIGHT_CASE_QUERY_KEY);
      if (clean !== location.search) {
        history.replace({ pathname: location.pathname, search: clean });
      }
      lastScrolledHighlightIdRef.current = highlightRowId;
    }, 200);

    const dimT = window.setTimeout(() => {
      setHighlightRowId(null);
      lastScrolledHighlightIdRef.current = null;
    }, 3000);

    return () => {
      clearTimeout(scrollT);
      clearTimeout(dimT);
    };
  }, [
    highlightRowId,
    caseDetails,
    loading,
    location.search,
    location.pathname,
    history,
  ]);

  const rowClassName = useCallback(
    (record: CaseRow) =>
      `cursor-pointer${
        record.id === highlightRowId ? " cases-table-row--highlight" : ""
      }`,
    [highlightRowId]
  );

  return { rowClassName };
}
